import React from "react";
import {
  ArrayField,
  ChipField,
  Datagrid,
  DatagridConfigurable,
  DateField,
  FunctionField,
  List,
  NumberField,
  SelectField,
  SelectInput,
  SingleFieldList,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  useResourceContext,
  useTranslate,
} from "react-admin";
import Box from "@mui/material/Box";

import { ExistsSpecialConditionsField } from "../components/exists_special_conditions_field";
import { formatStringAsCurrency } from "../utils/locale";
import { UndecidedOrFreeInput } from "../components/UndecidedOrFreeInput";
import { InvoiceRegistrationStatusField } from "../components/InvoiceRegistrationStatusField";
import { csvExporter } from "../infrastructure/csvExporter";
import { ShowToolbar } from "../components/ShowToolbar";
import { ListActions } from "../components/list_actions";
import { commonStyles } from "../components/common_styles";
import { getShunDateString } from "../components/harvest_plan";
import {
  formatDisplayStoragePeriodBonus,
  formatDisplayTradingVolume,
  formatDisplayUnitPrice,
  priceCalculationModeChoices,
} from "../utils/price_calculation";

const statuses = ["closed", "done", "canceled"];
const createStatusChoices = () =>
  statuses.map((status) => ({
    id: status,
    name: `resources.adjustment_deals.fields.statuses.${status}`,
  }));

function getValueOrUndecided(attr: string, formatFunction?: (val: any) => any) {
  const obj = attr.split(".")[0];
  const prop = attr.split(".")[1];

  return (record: any) => {
    if (formatFunction) {
      if (prop && record[obj]) {
        return record[obj][prop] == null ? "未確定" : formatFunction(record[obj][prop]);
      }

      return record[attr] == null ? "未確定" : formatFunction(record[attr]);
    } else {
      if (prop && record[obj]) {
        return record[obj][prop] == null ? "未確定" : record[obj][prop];
      }

      return record[attr] == null ? "未確定" : record[attr];
    }
  };
}

const AdjustmentDealFilter = [
  <SelectInput
    key="status"
    source="status"
    choices={createStatusChoices()}
    // react-adminでデフォルトフィルタの値を未選択にするとクラッシュする問題の回避
    alwaysOn
    parse={(value: any) => {
      if (value === "" || value == null) {
        return undefined;
      }
      return value;
    }}
    format={(value: any) => {
      if (value == null) {
        return "";
      }
      return value;
    }}
  />,
  <TextInput key="ids" source="ids" />,
  <TextInput key="producer_company_name" source="producer_company_name" />,
  <TextInput key="annum_name" source="annum_name" />,
  <TextInput key="prefecture_name" source="prefecture_name" />,
  <TextInput key="region_name" source="region_name" />,
  <UndecidedOrFreeInput key="brand_name" source="brand_name" />,
  <UndecidedOrFreeInput key="grade_name" source="grade_name" />,
  <UndecidedOrFreeInput key="unit_price" source="unit_price" onlyNumeric={true} />,
];

function getId(record: any) {
  return "R" + record["id"];
}

function getUnitPriceOrUndecided(record: any) {
  const value = formatDisplayUnitPrice(record);
  return value === "" || value == null ? "未確定" : value;
}

function getTradingVolumeOrUndecided(record: any) {
  const value = formatDisplayTradingVolume(record);
  return value === "" || value == null ? "未確定" : value;
}

function getStoragePeriodBonusOrUndecided(record: any) {
  const value = formatDisplayStoragePeriodBonus(record.storage_period, record.price_calculation_mode);
  return value === "" || value == null ? "未確定" : value;
}

export const AdjustmentDealList = (props: any) => {
  const resource = useResourceContext();
  const translate = useTranslate();
  const preferenceKey = "adjustment_deals";

  return (
    <List
      {...props}
      actions={
        <ListActions filters={AdjustmentDealFilter} preferenceKey={preferenceKey} savedFilterScope="adjustment_deals" />
      }
      filters={AdjustmentDealFilter}
      filterDefaultValues={{ status: "closed" }}
      sort={{ field: "id", order: "DESC" }}
      exporter={csvExporter(resource, translate)}
    >
      <DatagridConfigurable
        bulkActionButtons={false}
        preferenceKey={preferenceKey}
        rowClick="show"
        sx={{ "& .column-id": { minWidth: "200px" } }}
      >
        <FunctionField source="id" render={getId} />
        <SelectField source="status" choices={createStatusChoices()} sortable={false} />
        <FunctionField
          source="confirmation_timeframe"
          render={getValueOrUndecided("confirmation_timeframe", getShunDateString)}
          sortable={true}
        />
        <TextField source="producer.company_name" sortable={false} />
        <TextField source="annum.name" sortable={false} />
        <TextField source="prefecture.name" sortable={false} />
        <TextField source="region.name" sortable={false} />
        <FunctionField
          source="brand.name"
          render={getValueOrUndecided("brand.name")}
          sortable={false}
          textAlign="left"
        />
        <FunctionField
          source="grade.name"
          render={getValueOrUndecided("grade.name")}
          sortable={false}
          textAlign="left"
        />
        <FunctionField
          source="packaging_form.name"
          render={getValueOrUndecided("packaging_form.name")}
          sortable={false}
          textAlign="left"
        />
        <FunctionField
          source="trading_units"
          render={getValueOrUndecided("trading_units")}
          sortable={false}
          textAlign="right"
        />
        <FunctionField
          source="trading_sacks"
          render={getValueOrUndecided("trading_sacks")}
          sortable={false}
          textAlign="right"
        />
        <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} sortable={false} />
        <FunctionField source="unit_price" render={getUnitPriceOrUndecided} sortable={false} textAlign="right" />
        <FunctionField
          source="trading_volume_in_yen"
          render={getTradingVolumeOrUndecided}
          sortable={false}
          textAlign="right"
        />
        <ExistsSpecialConditionsField source="special_condition_ids" sortable={false} />
        <DateField source="decision_date" locales={"ja"} />
      </DatagridConfigurable>
    </List>
  );
};

export const AdjustmentDealShow = (props: any) => {
  return (
    <Show {...props} actions={<ShowToolbar listPath="/adjustment_deals" />}>
      <TabbedShowLayout>
        <Tab label="ui.adjustment_deals.tabs.info">
          <FunctionField source="id" render={getId} />
          <SelectField source="status" choices={createStatusChoices()} />
          <DateField source="decision_date" locales={"ja"} />
          <TextField source="producer.company_name" />
          <InvoiceRegistrationStatusField source="producer.invoice_registration_status" />
          <DateField source="created_at" locales={"ja"} showTime={true} />
        </Tab>
        <Tab path="contract" label="ui.adjustment_deals.tabs.contract">
          <TextField source="annum.name" />
          <TextField source="prefecture.name" />
          <TextField source="region.name" />
          <FunctionField source="brand.name" render={getValueOrUndecided("brand.name")} />
          <FunctionField source="grade.name" render={getValueOrUndecided("grade.name")} />
          <FunctionField source="packaging_form.name" render={getValueOrUndecided("packaging_form.name")} />
          <FunctionField source="trading_units" render={getValueOrUndecided("trading_units", formatStringAsCurrency)} />
          <FunctionField source="trading_sacks" render={getValueOrUndecided("trading_sacks", formatStringAsCurrency)} />
          <FunctionField source="trading_kg" render={getValueOrUndecided("trading_kg", formatStringAsCurrency)} />
          <FunctionField source="package_count" render={getValueOrUndecided("package_count", formatStringAsCurrency)} />
          <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} />
          <FunctionField source="unit_price" render={getUnitPriceOrUndecided} />
          <FunctionField source="trading_volume_in_yen" render={getTradingVolumeOrUndecided} />
          <FunctionField source="storage_period.name" render={getValueOrUndecided("storage_period.name")} />
          <FunctionField source="storage_period.bonus_in_yen" render={getStoragePeriodBonusOrUndecided} />
          <ArrayField source="grade_penalties">
            <Box sx={commonStyles.showPageInnerDatagrid}>
              <Datagrid bulkActionButtons={false} empty={null}>
                <NumberField source="grade.name" />
                <NumberField source="penalty_in_yen" emptyText="受け取らない" />
              </Datagrid>
            </Box>
          </ArrayField>
          <ArrayField source="special_conditions">
            <SingleFieldList linkType={false}>
              <ChipField source="name" />
            </SingleFieldList>
          </ArrayField>
          <TextField source="special_conditions_memo" component="pre" />
          <TextField source="adjustment_memo" component="pre" />
          <FunctionField
            source="confirmation_timeframe"
            render={getValueOrUndecided("confirmation_timeframe", getShunDateString)}
          />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
